import { BaseApi } from '@core/api';
import { PageDTO } from '@core/models';
import { CatalogCrop, FieldSeason, FieldSeasonData, Season } from '@core/models/seasons';
import { Dictionary } from 'redux-ngrx-entity';
import { FetchSeasonParams } from './season.models';

// Values that may arrive from upstream as a string but are not a usable country code.
const INVALID_COUNTRY_CODES = new Set(['', 'null', 'undefined', 'nan', 'none']);

function sanitizeCountryCode(value?: string): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed || INVALID_COUNTRY_CODES.has(trimmed.toLowerCase())) {
    return undefined;
  }
  return trimmed;
}

async function fetchCatalogCrops(country_code?: string) {
  if (!country_code) {
    return [];
  }

  const {
    data: { content: catalogCrops },
  } = await BaseApi.get<PageDTO<CatalogCrop>>('/catalog/crops', {
    params: {
      country: country_code,
    },
  });

  if (country_code === 'US') {
    const {
      data: { content: cwfCatalogCrops },
    } = await BaseApi.get<PageDTO<CatalogCrop>>('/catalog/crops', {
      params: {
        country: 'CWF',
      },
    });

    return [...catalogCrops, ...cwfCatalogCrops];
  }
  return catalogCrops;
}

async function fetchPropertySeasons(id: string) {
  const {
    data: { content: seasons },
  } = await BaseApi.get<PageDTO<Season>>(`/properties/${id}/seasons`);
  return seasons;
}

async function fetchFieldSeasons(id: string) {
  const {
    data: { content: fieldSeasons },
  } = await BaseApi.get<PageDTO<FieldSeason>>(`/properties/${id}/season-fields`, {
    params: { property_id: id },
  });
  return fieldSeasons;
}

async function fetchSeasonFields(propertyId: string, seasonId: string) {
  const {
    data: { content },
  } = await BaseApi.get<PageDTO<FieldSeason>>(`/seasons/${seasonId}/fields`, {
    params: { property_id: propertyId },
  });
  return content;
}

interface FetchSeasonsOptions {
  requireComplete?: boolean;
}

export async function fetchSeasons(
  data: FetchSeasonParams,
  { requireComplete = false }: FetchSeasonsOptions = {}
) {
  const country =
    sanitizeCountryCode(data.country_code) ?? sanitizeCountryCode(data.regulatory_zone);
  const catalogCropsPromise = fetchCatalogCrops(country);
  const seasonsPromise = fetchPropertySeasons(data.id);
  const fieldSeasonsPromise = fetchFieldSeasons(data.id);

  const [catalogCropsResult, seasonsResult, fieldSeasonsResult] = await Promise.allSettled([
    catalogCropsPromise,
    seasonsPromise,
    fieldSeasonsPromise,
  ]);

  if (requireComplete) {
    if (catalogCropsResult.status === 'rejected') throw catalogCropsResult.reason;
    if (seasonsResult.status === 'rejected') throw seasonsResult.reason;
    if (fieldSeasonsResult.status === 'rejected') throw fieldSeasonsResult.reason;
  }

  const catalogCrops = catalogCropsResult.status === 'fulfilled' ? catalogCropsResult.value : [];
  const seasons = seasonsResult.status === 'fulfilled' ? seasonsResult.value : [];
  const fieldSeasons = fieldSeasonsResult.status === 'fulfilled' ? fieldSeasonsResult.value : [];

  const enhancedCatalogCrops = catalogCrops.map((catalogCrop) => {
    return {
      ...catalogCrop,
      adapted_wk_slug:
        catalogCrop.name.find((nameItem) => nameItem.language === 'en')?.name ??
        catalogCrop.name[0].name,
    };
  });

  if (
    requireComplete &&
    seasons.some(
      (season) =>
        !season.deleted &&
        !enhancedCatalogCrops.some((catalogCrop) =>
          catalogCrop.localized_crops.some((localizedCrop) => localizedCrop.id === season.crop_id)
        )
    )
  ) {
    throw new Error('A non-deleted season could not be matched to the crop catalog');
  }

  const dataPromises = seasons.map<Promise<FieldSeasonData>>(async (season) => {
    const catalogCrop = enhancedCatalogCrops.find((catalogCrop) =>
      catalogCrop.localized_crops.some((locCrop) => locCrop.id === season.crop_id)
    );
    const seasonFields = await fetchSeasonFields(data.id, season.id);

    const fieldSeasonsMap = fieldSeasons.map((fieldSeason) => {
      return {
        fieldSeasonId: fieldSeason.id,
        fieldId: fieldSeason.field_id,
        seasonId: fieldSeason.season_id,
      };
    });

    return {
      id: season.id,
      season: {
        ...season,
        catalogCrop: catalogCrop,
      },
      fieldsSeason: seasonFields.reduce<Dictionary<FieldSeason>>((acc, fieldSeason) => {
        const fieldSeasonId = fieldSeasonsMap.find(
          (item) => item.fieldId === fieldSeason.id && item.seasonId === season.id
        )?.fieldSeasonId;

        acc[fieldSeason.id] = {
          ...fieldSeason,
          field_season_id: fieldSeasonId ?? '',
          season: {
            ...season,
            catalogCrop: catalogCrop,
          },
        };
        return acc;
      }, {}),
      uploaded: false,
    };
  });

  const seasonData = await Promise.allSettled(dataPromises);
  if (requireComplete) {
    const failedSeason = seasonData.find((result) => result.status === 'rejected');
    if (failedSeason?.status === 'rejected') throw failedSeason.reason;
  }

  const filteredData = seasonData.reduce<FieldSeasonData[]>((acc, seasonData) => {
    if (seasonData.status === 'fulfilled') {
      const { season } = seasonData.value;
      if (!season.deleted && season.catalogCrop) {
        acc.push(seasonData.value);
      }
    }
    return acc;
  }, []);

  return { catalogCrops: enhancedCatalogCrops, filteredData };
}
